import { useEffect, useRef, useState } from "react";
import { LandingNav } from "./landing/LandingNav";
import { LandingHero } from "./landing/LandingHero";
import { LandingFunnel, LandingRadar, LandingEngine, LandingWaterfall, LandingDocs, LandingTrust, LandingFinalCta, LandingFooter } from "./landing/LandingSections";
import { LandingHow, LandingPricing } from "./landing/LandingHow";
import "./landing/landing.css";

const VIDEO_MP4 = "/demo.mp4?v=3";
const VIDEO_WEBM = "/demo.webm?v=3";
const VTT_SRC = "/demo.it.vtt?v=3";
const POSTER_SRC = "/demo-poster.jpg";

const pickFormat = () => {
    const probe = document.createElement("video");
    if (probe.canPlayType('video/mp4; codecs="avc1.42E01E, mp4a.40.2"')) return { url: VIDEO_MP4, mime: "video/mp4" };
    if (probe.canPlayType('video/webm; codecs="vp9, opus"')) return { url: VIDEO_WEBM, mime: "video/webm" };
    return { url: VIDEO_MP4, mime: "video/mp4" };
};

export default function Intro() {
    const videoRef = useRef(null);
    const [started, setStarted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [vttSrc, setVttSrc] = useState(null);
    const srcsRef = useRef(null);
    const pendingRef = useRef(false);

    useEffect(() => {
        let alive = true;
        const t = setTimeout(async () => {
            let next;
            const fmt = pickFormat();
            try {
                const [vb, tb] = await Promise.all([
                    fetch(fmt.url).then((r) => { if (!r.ok) throw new Error("video"); return r.blob(); }),
                    fetch(VTT_SRC).then((r) => (r.ok ? r.blob() : null)).catch(() => null),
                ]);
                next = {
                    video: URL.createObjectURL(new Blob([vb], { type: fmt.mime })),
                    vtt: tb ? URL.createObjectURL(new Blob([tb], { type: "text/vtt" })) : null,
                };
            } catch {
                next = { video: fmt.url, vtt: null };
            }
            if (!alive) return;
            srcsRef.current = next;
            if (next.vtt) setVttSrc(next.vtt);
            if (pendingRef.current) startPlayback(next);
        }, 600);
        return () => { alive = false; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startPlayback = (srcs) => {
        const v = videoRef.current;
        if (!v) return;
        pendingRef.current = false;
        setLoading(false);
        if (v.getAttribute("src") !== srcs.video) {
            v.src = srcs.video;
            v.load();
        }
        v.muted = false;
        v.volume = 1;
        setStarted(true);
        v.play().catch(() => {});
    };

    const playVideo = () => {
        const v = videoRef.current;
        if (!v) return;
        document.getElementById("demo")?.scrollIntoView({ behavior: "smooth", block: "center" });
        if (srcsRef.current) {
            startPlayback(srcsRef.current);
            return;
        }
        try { v.load(); } catch { /* unlock media on mobile */ }
        pendingRef.current = true;
        setLoading(true);
    };

    return (
        <div className="min-h-screen bg-[#050505] antialiased lp-body">
            <LandingNav />
            <LandingHero onWatchDemo={playVideo} />
            <LandingFunnel />
            <LandingRadar />
            <LandingHow started={started} loading={loading} onPlay={playVideo} videoRef={videoRef} vttSrc={vttSrc} posterSrc={POSTER_SRC} />
            <LandingEngine />
            <LandingWaterfall />
            <LandingDocs />
            <LandingTrust />
            <LandingPricing />
            <LandingFinalCta />
            <LandingFooter />
        </div>
    );
}
